<template>
  <div
    class="event halloween-event-target"
    :class="eventClasses"
    :data-halloween-event="halloweenEventKey"
    @click="showCommentInput"
  >
    <HalloweenWeb v-if="showHalloweenWeb" :animate="animateHalloweenWeb" />
    <SQDCEventActionModalComponent
      v-if="showEventModal && siteId && eventType"
      :source="{
        kind: 'event-edit',
        eventId: event.id,
        eventType,
        workflowInstanceId: workflowContext?.instance.workflowInstanceId,
        workflowStepId: workflowContext?.step.workflowStepId,
      }"
      :site-id="siteId"
      :board-id="boardId"
      @removed-from-board="handleRemovedFromBoard"
      @close="showEventModal = false"
    />

    <EventActionRowLayout class="event-row-layout h-100" :side-class="recentlyChanged">
      <template #id>
        <div class="id lisaui:flex lisaui:items-center header">
          <IDBadge
            :id="event.oldId ?? event.id"
            :color="eventTypeConfig?.idColor"
            class="lisaui:text-center IDBadgeWidth"
            :prefix="event.oldId ? '' : getEventPrefix(eventTypes, event.typeId)"
            @click="showEventModal = true"
          />
        </div>
      </template>
      <template #header>
        <div class="lisaui:flex lisaui:items-center header">
          <div class="owner">
            <slot name="owner">
              <UserBadge
                :user="event.Creator"
                :event="event"
                class="bounceTag"
                @click.stop="setUserFilter(event.Creator, $event)"
              />
            </slot>
          </div>

          <div v-if="eventButtonModuleBadges.length > 0" class="button-module-badges">
            <span
              v-for="badge in eventButtonModuleBadges"
              :key="badge.key"
              class="button-module-badge"
              :class="`button-module-badge--${badge.severity}`"
            >
              <span class="button-module-badge__icon">
                <i :class="badge.icon" />
              </span>
              <span class="button-module-badge__label">
                {{ badge.label }}
              </span>
            </span>
          </div>

          <div class="grow">
            <slot name="leadingHeaderAdditions" />
          </div>

          <div
            class="lisaui:flex lisaui:items-center lisaui:justify-end flags text-right text-truncate lisaui:gap-1"
          >
            <FlagBadge
              v-for="flag in sortedFlags"
              :key="flag.id"
              class="chipStyleEAB bounceTag lisaui:cursor-pointer"
              :flag="flag"
              @click.stop="setFlagFilter(flag, $event)"
            >
              <template v-if="ifEventIncludedOverFlag(flag)" #addition>
                <InfoTooltip
                  :title="t('components.event-action.issue.AbstractIssue.flagTooltipTitle')"
                  class="lisaui:p-0 lisaui:m-0"
                  :text="
                    t('components.event-action.issue.AbstractIssue.flagTooltipTextBoard', {
                      name: originBoard?.name,
                      id: originBoard?.id,
                    })
                  "
                />
              </template>
            </FlagBadge>
            <EisenhowerChip
              v-model="eisenhowerPriority"
              :disabled="readOnly"
              :event-prefix="getEventPrefix(eventTypes, event.typeId)"
              :event="event"
              @click.stop="setPriorityFilter(eisenhowerPriority, $event)"
            />
          </div>
          <div>
            <AttachmentBadge
              :files="[
                ...(event.Folder?.files.map((file) => ({ ...file, name: file.fileName })) ?? []),
              ]"
            />
          </div>
          <div class="deadline">
            <slot name="deadline">
              <DeadlineBadge :deadline="event.occurrenceDate" />
            </slot>
          </div>

          <slot name="trailingHeaderAdditions" />
        </div>
      </template>

      <template #left>
        <div class="linked lisaui:flex h-100 justify-content-around align-items-start">
          <div
            v-if="linkedEvents.length > 0"
            class="linked-events-badge"
            @click.stop="setLinkedEventFilter"
          >
            <InfoTooltip
              :text="
                linkedEvents
                  .map(
                    (linkedEvent) =>
                      linkedEvent.oldId ??
                      `${getEventPrefix(eventTypes, linkedEvent.typeId)}${linkedEvent.id}`,
                  )
                  .join(', ')
              "
              icon="fas fa-link"
              :title="t('components.event-action.issue.AbstractIssue.linkedEvents')"
            >
              <template #activator>
                <Badge color="white">
                  <i class="fas fa-link" aria-hidden="true" />
                  {{ linkedEvents.length }}
                </Badge>
              </template>
            </InfoTooltip>
          </div>
        </div>
      </template>

      <template v-if="hasEventContext" #context>
        <div class="event-context">
          <span v-if="latestBoardTransferText" class="event-context__transfer">
            {{ latestBoardTransferText }}
          </span>
          <template v-if="showOriginBoardButton">
            <span v-if="originBoard?.name == null" class="event-context__origin">
              {{ t("global.deleted") }}
            </span>
            <span
              v-else
              :title="
                hasOriginBoardAccess
                  ? undefined
                  : t('components.kpiBoard.BoardHierarchy.BoardDisabled')
              "
              class="event-context__origin lisaui:inline-block"
            >
              <LISAButton
                style="font-size: 1em"
                class="p-button-outlined lisaui:border-0 lisaui:p-0"
                :disabled="!hasOriginBoardAccess"
                :title="hasOriginBoardAccess ? buttonTitle : undefined"
                @click="
                  $router.push({
                    name: 'EventActionBoardNew',
                    params: {
                      boardId: originBoard?.id.toString() ?? '',
                    },
                    query: {
                      scrollToEntity: 'event',
                      scrollToType: event.__typename,
                      scrollToId: event.id.toString(),
                    },
                  })
                "
              >
                <span class="text-truncate">{{ originBoard?.name }}</span>
              </LISAButton>
            </span>
          </template>
        </div>
      </template>

      <template #content>
        <div class="content">
          <div
            v-if="eventRowFieldsOnEAB.length > 0"
            class="event-row-fields"
            :class="{ single: eventRowFieldsOnEAB.length === 1 }"
          >
            <template v-if="eventRowFieldsOnEAB.length === 1">
              <div class="event-row-field-value single-value">
                <span
                  v-if="eventRowFieldsOnEAB[0].badge"
                  class="button-module-badge"
                  :class="`button-module-badge--${eventRowFieldsOnEAB[0].badge.severity}`"
                >
                  <span class="button-module-badge__icon">
                    <i :class="eventRowFieldsOnEAB[0].badge.icon" />
                  </span>
                  <span class="button-module-badge__label">
                    {{ eventRowFieldsOnEAB[0].badge.label }}
                  </span>
                </span>
                <SQDCIshikawaDiagram
                  v-else-if="eventRowFieldsOnEAB[0].ishikawaDiagram"
                  class="event-row-field-diagram"
                  :name="eventRowFieldsOnEAB[0].ishikawaDiagram.name"
                  :reasons="eventRowFieldsOnEAB[0].ishikawaDiagram.reasons"
                  :areas="eventRowFieldsOnEAB[0].ishikawaDiagram.areas"
                />
                <template v-else>
                  {{ eventRowFieldsOnEAB[0].value }}
                </template>
              </div>
            </template>
            <template v-else>
              <template v-for="field in eventRowFieldsOnEAB" :key="field.key">
                <div class="event-row-field-label text-truncate">
                  {{ field.label }}
                </div>
                <div
                  class="event-row-field-value"
                  :class="{ 'text-truncate': !field.ishikawaDiagram }"
                >
                  <span
                    v-if="field.badge"
                    class="button-module-badge"
                    :class="`button-module-badge--${field.badge.severity}`"
                  >
                    <span class="button-module-badge__icon">
                      <i :class="field.badge.icon" />
                    </span>
                    <span class="button-module-badge__label">
                      {{ field.badge.label }}
                    </span>
                  </span>
                  <SQDCIshikawaDiagram
                    v-else-if="field.ishikawaDiagram"
                    class="event-row-field-diagram"
                    :name="field.ishikawaDiagram.name"
                    :reasons="field.ishikawaDiagram.reasons"
                    :areas="field.ishikawaDiagram.areas"
                  />
                  <template v-else>
                    {{ field.value }}
                  </template>
                </div>
              </template>
            </template>
          </div>
          <div v-if="negativeAuditEvidences.length" class="audit-negative-evidences">
            <div
              v-for="evidence in negativeAuditEvidences"
              :key="evidence.id"
              class="audit-negative-evidence"
            >
              <strong>{{ evidence.Question.text }}</strong>
              <span v-if="evidence.text">{{ evidence.text }}</span>
              <AttachmentBadge
                v-if="evidence.Files.length"
                :files="evidence.Files.map(({ File }) => ({ ...File, name: File.fileName }))"
              />
            </div>
          </div>
          <SQDCWorkflowProceedButtons
            v-if="workflowContext != null && siteId != null"
            :workflow-instance-id="workflowContext.instance.workflowInstanceId"
            :workflow-step-id="workflowContext.step.workflowStepId"
            :event="event"
            :small="true"
            :site-id="siteId"
            :board-id="currentBoardId"
            :disabled="readOnly"
          />
          <div>
            <EventActionCommentSection
              v-if="isModifierSet(ModifierType.eventComments)"
              v-model:comment-visible="commentVisible"
              class="hideOnMobile w-100"
              :event-id="event.id"
              :revisions="event.SQDCEventRevisions"
              :event-action-type-fields="eventType?.SQDCEventActionTypeFields ?? []"
              :board-event-connections="event.BoardEventConnection"
              :archived="readOnly"
              :original-board-id="event.originalBoardId"
            />
          </div>
        </div>
      </template>

      <template #controls>
        <div class="control-rail">
          <div class="event-control-buttons-boundary">
            <div class="event-control-buttons lisaui:flex lisaui:items-center lisaui:gap-2">
              <EventConfigButtonList v-if="hasSQDCWrite" :event="event" :event-types="eventTypes" />
              <EventActionButtonList
                v-if="siteId && hasSQDCWrite"
                :event="event"
                :site-id="siteId"
                :board-id="currentBoardId"
              />
            </div>
          </div>
          <i
            v-if="
              commentVisible && event.deletedAt == null && isModifierSet(ModifierType.eventComments)
            "
            class="fas fa-angle-up collapseButton"
            style="font-size: 1.5em"
            @click.stop="handleOneLine"
          />
        </div>
      </template>
    </EventActionRowLayout>
    <slot name="editComponent" />
  </div>
</template>

<script setup lang="ts">
import HalloweenWeb from "@/components/shared/HalloweenWeb.vue";
import { halloweenArchiveTarget, halloweenWebbedEvents } from "@/functions/halloweenSpider";
import { computed, type PropType, inject, ref, watch } from "vue";
import { LISA_DATE_FULL, useTranslation } from "@/language/translation";
import LISAButton from "@LISAPrime/LISAButton.vue";
import {
  SQDCBoardEventStatus,
  SQDCEventActionTypeFieldType,
  useGetSiteIdForBoardQuery,
  useSQDCEventActionTypesQuery,
  type ArchiveRange,
  type EisenhowerPriority,
  type EventListEventFieldsFragment,
} from "@/graphql";
import {
  ArchiveRangeKey,
  BoardIdInjectionKey,
  BoardIncludedFlagIdsInjectionKey,
  getRecentlyChangedAlternative,
  RecentlyChangedAlternativeEnum,
} from "@/views/event-action/EventActionBoardInjectionKeys";
import InfoTooltip from "@/components/shared/InfoTooltip.vue";
import { getBoardNameFromCache, useGetBoardInfo } from "@/modules/sqdc/helper/EABConfigHelper";
import IDBadge from "@/components/badges/IDBadge.vue";
import UserBadge from "@/components/badges/UserBadge.vue";
import DeadlineBadge from "@/components/badges/DeadlineBadge.vue";
import {
  getEventPrefix,
  getCardsForRevision,
  type EABEventType,
  type EABEventTypeType,
} from "./EventActionHelper";
import EventActionCommentSection from "./EventActionCommentSection.vue";
import EventConfigButtonList from "./action-buttons/EventConfigButtonList.vue";
import EventActionButtonList from "./action-buttons/EventActionButtonList.vue";
import { useSQDCWorkflowInstancesForItem } from "../../workflow/useSQDCWorkflows";
import FlagBadge from "@/components/badges/FlagBadge.vue";
import AttachmentBadge from "@/components/badges/AttachmentBadge.vue";
import Badge from "@/components/badges/BadgeComponent.vue";
import SQDCEventActionModalComponent from "./SQDCEventActionModalComponent.vue";
import SQDCWorkflowProceedButtons from "./SQDCWorkflowProceedButtons.vue";
import SQDCIshikawaDiagram from "./modals/SQDCIshikawaDiagram.vue";
import { isRecentlyChanged } from "@/components/event-action/helper/EventBoardRecentlyChangedHelper";
import { DateTime } from "luxon";
import {
  EventFilterInjectionKey,
  ShowArchiveInjectionKey,
  type EventFilter,
} from "./filter/FilterTypes";
import IDFilter from "./filter/filterImplementations/IDFilter";
import CreatorFilter from "./filter/filterImplementations/CreatorFilter";
import FlagsFilter from "./filter/filterImplementations/FlagsFilter";
import PriorityFilter from "./filter/filterImplementations/PriorityFilter";
import { ModifierType } from "@/functions/userModifierEnum.ts";
import isModifierSet from "@/functions/userModifier.ts";
import { getSQDCWorkflowItemContext } from "../../workflow/domain";
import { hasConfiguredAccessRight } from "@/functions/accessRightHelper";
import { hasRight } from "@/functions/authorizationState";
import {
  getSQDCButtonModuleDisplayOption,
  isSQDCButtonModuleType,
  type SQDCButtonModuleOption,
} from "@/modules/sqdc/helper/SQDCButtonModuleHelper";
import {
  getEABEventFieldRows,
  useEABEventFieldDisplays,
  type EABDisplayField as EventRowField,
  type EABDisplayFieldRow as FieldRow,
} from "./EABEventFieldHelper";
import EisenhowerChip from "./EisenhowerChip.vue";
import EventActionRowLayout from "./EventActionRowLayout.vue";
import { TimeZoneInjectionKey } from "@/functions/timezoneUtil";

const props = defineProps({
  event: {
    type: Object as PropType<EABEventType>,
    required: true,
  },
  eventTypes: {
    type: Array as PropType<EABEventTypeType[]>,
    required: false,
    default: () => [],
  },
  boardId: {
    type: Number,
    required: false,
    default: undefined,
  },
  siteId: {
    type: Number,
    required: false,
    default: undefined,
  },
});
const { instances: workflowInstances } = useSQDCWorkflowInstancesForItem(
  computed(() => ({ eventId: props.event.id })),
);
const workflowContext = computed(() =>
  getSQDCWorkflowItemContext(workflowInstances.value, { eventId: props.event.id }),
);
const eventWithReadonlyWorkflowValues = computed<EABEventType>(() => {
  const bindings = (workflowContext.value?.step.node.variableBindings ?? []).filter(
    ({ readonly }) => readonly,
  );
  if (bindings.length === 0) return props.event;
  const fieldIds = new Set(bindings.map(({ fieldId }) => fieldId));
  return {
    ...props.event,
    SQDCEventTextFields: [
      ...props.event.SQDCEventTextFields.filter(({ fieldId }) => !fieldIds.has(fieldId)),
      ...bindings.flatMap((binding) => {
        const value = workflowContext.value?.instance.variableValues?.[binding.variableId];
        return value == null ? [] : [{ fieldId: binding.fieldId, value }];
      }),
    ],
  };
});

const commentVisible = ref(false);
function showCommentInput() {
  if (!readOnly.value) commentVisible.value = true;
}
const emit = defineEmits<{
  click: [];
  openEditComponent: [];
  EisenhowerMatrixModal: [];
  removedFromBoard: [eventId: number];
}>();
const { t, te } = useTranslation();
const eisenhowerPriority = ref<EisenhowerPriority>(props.event.eisenhowerPriority);
watch(
  () => [props.event.id, props.event.eisenhowerPriority] as const,
  ([, priority]) => {
    eisenhowerPriority.value = priority;
  },
);
const recentlyChangedAlternativeKey = ref(getRecentlyChangedAlternative());
const sortedFlags = computed(() => {
  return [...props.event.FlagEventConnection]
    .map((el) => el.flag)
    .sort((a, b) => {
      const displayNameA = te(`dynamic.${a.abbreviationId}`)
        ? t(`dynamic.${a.abbreviationId}`)
        : te(`dynamic.${a.displayNameId}`)
          ? t(`dynamic.${a.displayNameId}`)
          : (a.name ?? `[${t("global.id")}:${a.id}]`);
      const displayNameB = te(`dynamic.${b.abbreviationId}`)
        ? t(`dynamic.${b.abbreviationId}`)
        : te(`dynamic.${b.displayNameId}`)
          ? t(`dynamic.${b.displayNameId}`)
          : (b.name ?? `[${t("global.id")}:${b.id}]`);
      return displayNameA.localeCompare(displayNameB);
    });
});

const propEventType = computed(() =>
  props.eventTypes.find((typeIteratee) => typeIteratee.id === props.event.typeId),
);
const { result: eventTypeResult } = useSQDCEventActionTypesQuery(
  computed(() => ({ ids: [props.event.typeId] })),
  computed(() => ({
    enabled: propEventType.value == null,
    fetchPolicy: "cache-first",
  })),
);
const eventTypeConfig = computed(
  () => propEventType.value ?? eventTypeResult.value?.SQDCEventActionTypes[0],
);
const eventType = computed(() => {
  const type = eventTypeConfig.value;

  if (type == null) {
    return null;
  }
  return {
    ...type,
    cards: getCardsForRevision(type.Cards, props.event.revision),
    SQDCEventActionTypeFields: type.SQDCEventActionTypeFields.filter(
      (field) =>
        field.addedByRevision <= props.event.revision &&
        (field.removedByRevision == null || field.removedByRevision > props.event.revision),
    ),
  };
});

type ButtonModuleBadge = Pick<SQDCButtonModuleOption, "icon" | "severity"> & {
  key: number;
  label: string;
};

const { getDisplays: getEventFieldDisplays } = useEABEventFieldDisplays(
  computed(() => [eventWithReadonlyWorkflowValues.value]),
);
const eventRowFieldsOnEAB = computed(() =>
  getEventFieldDisplays(eventWithReadonlyWorkflowValues.value, eventType.value, siteId.value),
);
const negativeAuditEvidences = computed(() => {
  const visibleFields = new Set(
    eventType.value?.SQDCEventActionTypeFields.filter(
      (field) => field.type === SQDCEventActionTypeFieldType.AUDIT_QUESTIONNAIRE && field.showOnEAB,
    ).map((field) => field.id) ?? [],
  );
  return props.event.AuditEvidences.filter(
    (evidence) => evidence.value === 0 && visibleFields.has(evidence.fieldId),
  );
});

const eventButtonModuleBadges = computed(() => {
  const fields =
    eventType.value?.SQDCEventActionTypeFields.filter((field) =>
      isSQDCButtonModuleType(field.type),
    ) ?? [];

  return fields
    .filter((field) => hasConfiguredAccessRight(field.visibleForAdminOnly, siteId.value))
    .map((field) => getButtonModuleBadge(field, getEventRowFieldRows(field), "nothing"))
    .filter((badge): badge is ButtonModuleBadge => badge != null);
});

function getEventRowFieldRows(field: EventRowField): FieldRow[] {
  return getEABEventFieldRows(eventWithReadonlyWorkflowValues.value, field);
}

function getButtonModuleBadge(
  field: EventRowField,
  rows: FieldRow[],
  fallbackValue?: unknown,
): ButtonModuleBadge | undefined {
  if (!isSQDCButtonModuleType(field.type)) {
    return undefined;
  }
  const value = rows.find((row) => typeof row.value === "string")?.value ?? fallbackValue;
  const option = getSQDCButtonModuleDisplayOption(field.type, value);
  return option == null
    ? undefined
    : {
        key: field.id,
        icon: option.icon,
        label: t(option.labelKey),
        severity: option.severity,
      };
}

const showEventModal = ref(false);

function handleRemovedFromBoard(eventId: number) {
  showEventModal.value = false;
  emit("removedFromBoard", eventId);
}
const archiveRange = inject(ArchiveRangeKey, ref<ArchiveRange | undefined>(undefined));
const recentlyChanged = computed(() => {
  if (!isRecentlyChanged(props.event.updatedAt)) {
    return {};
  }
  switch (recentlyChangedAlternativeKey.value) {
    case RecentlyChangedAlternativeEnum.NORMAL:
      return { recentlyChanged: true };
    case RecentlyChangedAlternativeEnum.GREEN_WAVES:
      return { recentlyChangedAlternative: true };
    default:
      return undefined;
  }
});
const linkedEvents = computed(() => [
  ...props.event.SQDCEventLinkageSources.map((el) => ({
    id: el.targetEventId,
    typeId: el.targetEvent?.typeId,
    oldId: el.targetEvent?.oldId,
  })),
  ...props.event.SQDCEventLinkageTargets.map((el) => ({
    id: el.sourceEventId,
    typeId: el.sourceEvent?.typeId,
    oldId: el.sourceEvent?.oldId,
  })),
]);
const eventFilters = inject(EventFilterInjectionKey, undefined);
const showArchive = inject(ShowArchiveInjectionKey, undefined);
function setLinkedEventFilter() {
  if (eventFilters == null || showArchive == null) {
    return;
  }
  eventFilters.value = {
    updatedAt: DateTime.now(),
    eventFilters: [
      new IDFilter([...new Set([...linkedEvents.value.map(({ id }) => id), props.event.id])]),
    ],
    type: eventFilters.value.type,
  };
  showArchive.value = true;
}
const eventClasses = computed(() => ({
  deleted: props.event.deletedAt != null && archiveRange?.value === undefined,
}));
function handleOneLine() {
  commentVisible.value = false;
}
const currentIncludedFlagIds = inject(BoardIncludedFlagIdsInjectionKey, ref<null>(null));
function ifEventIncludedOverFlag(flag: EventListEventFieldsFragment["flags"][number]): boolean {
  /**
   * we return early when the event is displayed on the dashboard because of a false-positive result
   */
  return (
    props.boardId != null &&
    !props.event.BoardEventConnection.some((con) => con.boardId === props.boardId) &&
    !!currentIncludedFlagIds.value?.includes(flag.id)
  );
}

function applyEventFilter(filter: EventFilter) {
  if (eventFilters == null) {
    return;
  }
  eventFilters.value = {
    updatedAt: DateTime.now(),
    eventFilters: [filter],
    type: eventFilters.value.type,
  };
}

function setUserFilter(owner: EventListEventFieldsFragment["owner"], evt: MouseEvent) {
  if ((evt.ctrlKey || evt.metaKey) && owner != null) {
    applyEventFilter(new CreatorFilter([owner.id]));
  }
}
function setFlagFilter(flag: EventListEventFieldsFragment["flags"][number], evt: MouseEvent) {
  if (evt.ctrlKey || evt.metaKey) {
    applyEventFilter(new FlagsFilter([flag.id]));
  }
}
function setPriorityFilter(priority: EisenhowerPriority, evt: MouseEvent) {
  if (evt.ctrlKey || evt.metaKey) {
    applyEventFilter(new PriorityFilter([priority]));
  }
}

const originBoardConnection = computed(() => {
  return props.event.BoardEventConnection.find(
    (con) => con.status === SQDCBoardEventStatus.CREATED,
  );
});

const { boardName, hasBoardAccess: hasOriginBoardAccess } = useGetBoardInfo(
  computed(() => originBoardConnection.value?.boardId),
);
const originBoard = computed(() => {
  if (originBoardConnection.value == null) {
    return undefined;
  }
  return {
    id: originBoardConnection.value.boardId,
    name: boardName.value,
  };
});
const injectedBoardId = inject(BoardIdInjectionKey, ref<number | null>(null));
const currentBoardId = computed(() =>
  props.boardId == null || Number.isNaN(Number(props.boardId))
    ? (injectedBoardId.value ?? undefined)
    : Number(props.boardId),
);
const timeZone = inject(TimeZoneInjectionKey);
const latestBoardTransfer = computed(
  () =>
    props.event.BoardEventConnection.filter(
      ({ status }) =>
        status === SQDCBoardEventStatus.ESCALATED || status === SQDCBoardEventStatus.DELEGATED,
    ).toSorted((left, right) => right.createdAt.localeCompare(left.createdAt))[0],
);
const latestBoardTransferText = computed(() => {
  const connection = latestBoardTransfer.value;
  if (connection == null) return "";

  const statusKey =
    connection.status === SQDCBoardEventStatus.ESCALATED ? "escalatedText" : "delegatedText";
  const isCurrentBoard = connection.boardId === currentBoardId.value;
  const message = t(
    `components.event-action.issue.BoardOriginArrow.${statusKey}${isCurrentBoard ? "Own" : ""}`,
    {
      createdAt: DateTime.fromISO(connection.createdAt)
        .setZone(timeZone?.value)
        .toLocaleString(LISA_DATE_FULL),
      boardName: getBoardNameFromCache(connection.boardId) ?? t("global.na"),
    },
  );
  const reason = connection.reason?.trim();
  return reason
    ? `${message} ${t("components.event-action.issue.BoardOriginArrow.reason", { reason })}`
    : message;
});
const showOriginBoardButton = computed(
  () =>
    originBoard.value?.id != null &&
    currentBoardId.value != null &&
    Number(originBoard.value.id) !== currentBoardId.value,
);
const hasEventContext = computed(
  () => latestBoardTransferText.value !== "" || showOriginBoardButton.value,
);
const { result: originBoardResult } = useGetSiteIdForBoardQuery(
  computed(() => ({ boardId: originBoard.value?.id ?? 0 })),
  computed(() => ({
    enabled: props.siteId == null && originBoard.value?.id != null,
    fetchPolicy: "cache-first",
  })),
);
const siteId = computed(() => props.siteId ?? originBoardResult.value?.GetSQDCBoard.siteId);

const halloweenEventKey = computed(() => `SQDCEvent:${props.event.id}`);
const initiallyArchivedAt = props.event.deletedAt;
const showHalloweenWeb = computed(
  () =>
    isModifierSet(ModifierType.halloweenSpider) &&
    props.event.deletedAt != null &&
    (initiallyArchivedAt === props.event.deletedAt ||
      halloweenWebbedEvents.value.get(halloweenEventKey.value) === props.event.deletedAt),
);
const animateHalloweenWeb = computed(
  () =>
    initiallyArchivedAt !== props.event.deletedAt &&
    halloweenArchiveTarget.value?.key === halloweenEventKey.value,
);

const hasSQDCWrite = computed(() => hasRight("sqdc.write", siteId.value));
const readOnly = computed(() => props.event.deletedAt != null || !hasSQDCWrite.value);
const buttonTitle = computed(() => {
  return `${t(
    "components.event-action.issue.AbstractIssue.originalBoard",
  ).toString()} "${originBoard?.value?.name}"`;
});
</script>

<style lang="scss" src="@/components/event-action/shared.scss"></style>

<style lang="scss" scoped>
.halloween-event-target {
  position: relative;
}

.py-05 {
  padding-top: 0.125rem !important;
  padding-bottom: 0.125rem !important;
}
.event {
  color: var(--p-black) !important;

  .grow {
    display: flex;
    align-items: center;
  }

  .content {
    min-width: 0;
  }

  .event-row-fields {
    display: grid;
    grid-template-columns: max-content minmax(5em, 1fr);
    gap: 0.25rem 0.75rem;

    min-width: 0;
    font-weight: bold;

    &.single {
      display: block;
    }
  }

  .event-row-field-label {
    min-width: 0;
    color: var(--p-blue-700);
    font-weight: bold;
  }

  .event-row-field-value {
    min-width: 0;
    white-space: pre-wrap;
    word-break: break-word;

    &.single-value {
      width: 100%;
    }
  }

  .event-row-field-diagram {
    width: min(100%, 34rem);

    :deep(.ishikawa-diagram) {
      min-height: 8rem;
      max-height: 12rem;
    }
  }

  .button-module-badges {
    display: flex;
    flex: 0 1 min(24rem, 38%);
    align-items: center;
    gap: 0.35rem;
    min-width: 0;
    max-width: min(24rem, 38%);
    margin: 0 0.6rem 0 0.45rem;
    overflow: hidden;
  }

  .button-module-badge {
    display: inline-flex;
    align-items: stretch;
    min-width: 1.65rem;
    max-width: 100%;
    min-height: 1.2rem;
    overflow: hidden;
    color: var(--p-white);
    font-weight: 500;
    line-height: 1;
    vertical-align: middle;
    white-space: nowrap;
    border-radius: var(--p-border-radius-md);
  }

  .button-module-badges > .button-module-badge {
    flex: 1 1 0;
    max-width: 100%;
  }

  .button-module-badge__icon,
  .button-module-badge__label {
    display: inline-flex;
    align-items: center;
  }

  .button-module-badge__icon {
    flex: 0 0 1.65rem;
    justify-content: center;
    width: 1.65rem;
    background: rgb(0 0 0 / 18%);
  }

  .button-module-badge__label {
    flex: 1 1 auto;
    min-width: 0;
    padding: 0 0.7rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .button-module-badge--success {
    background: var(--p-green-600);
  }

  .button-module-badge--danger {
    background: var(--p-red-600);
  }

  .button-module-badge--secondary {
    background: var(--p-slate-600);
  }

  .button-module-badge--help {
    background: var(--p-purple-600);
  }

  .flags {
    flex: 0 1 auto;
    gap: 0.4rem;
    min-width: 0;
    margin-left: 0.2rem;
  }

  .linked-events-badge {
    margin-top: 0.25rem;
    cursor: pointer;
  }

  .event-context {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    min-height: 2rem;
  }

  .event-context__transfer {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .event-context__origin {
    flex: 0 0 auto;
    margin-left: auto;
  }

  .event-row-layout :deep(.event-action-row-layout__controls) {
    padding-top: 0;
  }

  .control-rail {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    height: 100%;
    min-width: 4rem;

    .event-control-buttons-boundary {
      flex: 1 1 auto;
      min-height: 0;
    }

    .event-control-buttons {
      position: sticky;
      top: 0;
      z-index: 1;
      height: 2rem;
    }

    .collapseButton {
      padding: 3px;
      cursor: pointer;
    }
  }

  @media (max-width: 48rem) {
    .event-row-fields:not(.single) {
      grid-template-columns: minmax(0, 1fr);
    }

    .button-module-badges {
      flex-basis: 100%;
      max-width: 100%;
      margin-right: 0;
      margin-left: 0;
    }
  }
}
.IDBadgeWidth {
  min-width: calc(8ch + 10.5px);
}
</style>

<style lang="scss">
.buttonGroup {
  .btn-sm {
    font-size: 0.875em !important;
  }
}
.chipStyleEAB {
  font-size: 0.855em !important;
  padding: 0.5em !important;
  line-height: 1 !important;
  font-weight: normal !important;
}
</style>
